import React from "react";
import type { WidgetResult } from "./types";
import { WidgetError } from "./WidgetError";
import { WidgetSkeleton } from "./WidgetSkeleton";

/**
 * State-aware wrapper for every dashboard widget.
 *
 * Renders one of four visuals based on `result.status`:
 *   - loading → <WidgetSkeleton>
 *   - empty   → muted icon + empty-state copy (per `emptyCopy` prop)
 *   - ready   → `children`
 *   - error   → <WidgetError> with the errorId and a retry button
 *
 * The frame's bounding box is stable across all four states so the page
 * does not reflow (design.md §Widget state contracts, Requirement 3.5).
 */
export interface WidgetFrameProps<T> {
  /** The discriminated union returned by every dashboard hook. */
  result: WidgetResult<T>;
  /**
   * Human-readable widget name, e.g. "Arrears". Threaded to <WidgetError>
   * so the error card can show "Arrears unavailable".
   */
  name: string;
  /**
   * Copy for the empty state. Defaults to "No data yet" per design.md
   * §Widget state contracts.
   */
  emptyCopy?: string;
  /**
   * Height reserved for the loading skeleton. Defaults to 140.
   */
  skeletonHeight?: number;
  /**
   * The ready-state content. Only rendered when `result.status === 'ready'`.
   */
  children: React.ReactNode;
}

export function WidgetFrame<T>({
  result,
  name,
  emptyCopy,
  skeletonHeight,
  children,
}: WidgetFrameProps<T>): JSX.Element {
  if (result.status === "loading") {
    return <WidgetSkeleton height={skeletonHeight} />;
  }

  if (result.status === "empty") {
    const copy = emptyCopy ?? "No data yet";
    return (
      <div
        role="status"
        data-testid="widget-empty"
        className="w-full h-full flex flex-col items-center justify-center gap-2 px-4 py-3 text-center"
      >
        <span aria-hidden="true" className="text-2xl text-gray-300">
          •
        </span>
        <p className="text-sm" style={{ color: "#6b7280" }}>
          {copy}
        </p>
      </div>
    );
  }

  if (result.status === "error") {
    return (
      <WidgetError
        errorId={result.errorId}
        name={name}
        onRetry={result.retry}
      />
    );
  }

  // status === 'ready'
  return <>{children}</>;
}
